import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  AllocationTask,
  AllocationTaskInEdit,
  isSameTask,
} from '../pages/allocations/components/DayAllocations/allocationTableUtility';
import { isTaskStartOrArrival } from '../services/isTaskStartOrArrival';

type InEditTask = {
  type: 'unallocated' | 'allocated';
  /**
   * 編集するタスク
   */
  primary: AllocationTaskInEdit;
  /**
   * 編集するタスクに付随するタスク
   */
  secondary: AllocationTaskInEdit;
  step: 1 | 2 | 3 | 4;
  /**
   * 移動元車両 ID
   */
  originCarId?: number;
  /**
   * 移動先車両 ID: 参照用
   */
  targetCarId?: number;
  /**
   * 設定した車両名: 表示用
   */
  selectedCarName?: string;
  /**
   * 移動元ドライバー ID
   */
  originUserId?: number;
  /**
   * 移動先ドライバー ID: 参照用
   */
  targetUserId?: number;
  /**
   * 設定したドライバー名: 表示用
   */
  selectedUserName?: string;
  /**
   * 移動操作かどうか
   */
  isMoving?: boolean;
} | null;
type State = {
  allocationTasks: AllocationTask[];
  allocationTasksBeforeUpdate: AllocationTask[];
  tasksReadyToUpdate: AllocationTask[];
  editState: InEditTask;
  editMode: boolean;
};
const initialState: State = {
  allocationTasks: [],
  allocationTasksBeforeUpdate: [],
  tasksReadyToUpdate: [],
  editState: null,
  editMode: false,
};

/**
 * Array.prototype.toSpliced は ES2023 の機能のため、現バージョンの TypeScript では利用できない。
 * その代替として配列コピー後に Array.prototype.splice を適用するラッパー関数
 *
 * @param arr array
 * @param start number
 * @param deleteCount number
 * @param items array
 */
function toSpliced<T>(arr: T[], start: number, deleteCount?: number): T[];
function toSpliced<T>(
  arr: T[],
  start: number,
  deleteCount: number,
  ...items: T[]
): T[];
function toSpliced<T>(
  arr: T[],
  start: number,
  deleteCount?: number,
  ...items: T[]
) {
  const copied = [...arr];
  if (deleteCount === undefined) {
    copied.splice(start, deleteCount);
    return copied;
  } else {
    copied.splice(start, deleteCount, ...items);
    return copied;
  }
}

const isTaskEditCompleted = (
  task: AllocationTaskInEdit
): task is AllocationTask => {
  if (task.type === 'allocated') {
    return task.startDate !== undefined && task.endDate !== undefined;
  } else {
    return (
      task.contractId !== undefined &&
      task.startDate !== undefined &&
      task.endDate !== undefined &&
      task.wasteItemId !== undefined &&
      task.workCategory !== undefined
    );
  }
};

export const CarAllocationEditSlice = createSlice({
  name: 'carAllocationEdit',
  initialState,
  reducers: {
    setAllocationTasks(state, action: PayloadAction<AllocationTask[]>) {
      state.allocationTasks = action.payload;
    },
    setStartOrArrivalTasksToReadyToUpdate(
      state,
      action: PayloadAction<AllocationTaskInEdit>
    ) {
      const task = action.payload;
      const { carId, userId } = task;
      if (!isTaskEditCompleted(task) || !isTaskStartOrArrival(task)) {
        return;
      }

      const currentItemIndex = state.allocationTasks.findIndex(
        (v) => isSameTask(v, task) && v.carId === carId && v.userId === userId
      );
      state.allocationTasks = toSpliced(
        state.allocationTasks,
        currentItemIndex,
        1,
        task
      );

      const currentItemInEditIndex = state.tasksReadyToUpdate.findIndex((v) =>
        isSameTask(v, task)
      );
      if (currentItemInEditIndex === -1) {
        state.tasksReadyToUpdate = [...state.tasksReadyToUpdate, task];
      } else {
        state.tasksReadyToUpdate = toSpliced(
          state.tasksReadyToUpdate,
          currentItemIndex,
          1,
          task
        );
      }
    },
    calcTasksReadyToUpdate(state) {
      if (!state.editState) {
        return;
      }
      const primary = state.editState.primary;
      const secondary = state.editState.secondary;
      if (!isTaskEditCompleted(primary) || !isTaskEditCompleted(secondary)) {
        return;
      }

      const currentPrimaryItemIndex = state.allocationTasks.findIndex((v) =>
        isSameTask(v, primary)
      );
      const currentSecondaryItemIndex = state.allocationTasks.findIndex((v) =>
        isSameTask(v, secondary)
      );
      // current と secondary の配置はセットなので片方が配車タスク一覧にあるならもう片方も存在する
      if (currentPrimaryItemIndex === -1) {
        state.allocationTasks = [...state.allocationTasks, primary, secondary];
      } else {
        state.allocationTasks = toSpliced(
          state.allocationTasks,
          currentPrimaryItemIndex,
          1,
          primary
        );
        state.allocationTasks = toSpliced(
          state.allocationTasks,
          currentSecondaryItemIndex,
          1,
          secondary
        );
      }

      const currentPrimaryEditItemIndex = state.tasksReadyToUpdate.findIndex(
        (v) => isSameTask(v, primary)
      );
      const currentSecondaryEditItemIndex = state.tasksReadyToUpdate.findIndex(
        (v) => isSameTask(v, secondary)
      );
      // current と secondary の配置はセットなので片方が配車タスク一覧にあるならもう片方も存在する
      if (currentPrimaryEditItemIndex === -1) {
        state.tasksReadyToUpdate = [
          ...state.tasksReadyToUpdate,
          primary,
          secondary,
        ];
      } else {
        state.tasksReadyToUpdate = toSpliced(
          state.tasksReadyToUpdate,
          currentPrimaryEditItemIndex,
          1,
          primary
        );
        state.tasksReadyToUpdate = toSpliced(
          state.tasksReadyToUpdate,
          currentSecondaryEditItemIndex,
          1,
          secondary
        );
      }
    },
    setSingleTaskReadyToUpdate(
      state,
      action: PayloadAction<AllocationTaskInEdit>
    ) {
      const task = action.payload;
      if (!isTaskEditCompleted(task)) {
        return;
      }
      const currentItemIndex = state.allocationTasks.findIndex((v) =>
        isSameTask(v, task)
      );
      if (currentItemIndex === -1) {
        state.allocationTasks = [...state.allocationTasks, task];
      } else {
        state.allocationTasks = toSpliced(
          state.allocationTasks,
          currentItemIndex,
          1,
          task
        );
      }
      const currentEditItemIndex = state.tasksReadyToUpdate.findIndex((v) =>
        isSameTask(v, task)
      );
      if (currentEditItemIndex === -1) {
        state.tasksReadyToUpdate = [...state.tasksReadyToUpdate, task];
      } else {
        state.tasksReadyToUpdate = toSpliced(
          state.tasksReadyToUpdate,
          currentEditItemIndex,
          1,
          task
        );
      }
    },
    startEditMode(state) {
      state.editMode = true;
      state.allocationTasksBeforeUpdate = [...state.allocationTasks];
    },
    setEditTask(state, action: PayloadAction<InEditTask>) {
      state.editState = action.payload;
    },
    resetAll(state) {
      state.allocationTasks = [...state.allocationTasksBeforeUpdate];
      state.tasksReadyToUpdate = [];
      state.editState = null;
      state.editMode = false;
    },
    removeEditTask(state) {
      state.editState = null;
    },
  },
});

export const {
  setAllocationTasks,
  calcTasksReadyToUpdate,
  setStartOrArrivalTasksToReadyToUpdate,
  setSingleTaskReadyToUpdate,
  startEditMode,
  setEditTask,
  resetAll,
  removeEditTask,
} = CarAllocationEditSlice.actions;
